import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidePanel } from 'hooks/useSidePanel';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { Grid, Typography } from '@mui/material';
import { DraftCard } from '../draft-card';
import { DraftCardMedia } from '../draft-card-media';
import { DraftCardMessage } from '../draft-card-message';
import UploadedAudioContent from 'components/uploaded-audio-content';
import UploadedFiles from 'components/uploaded-files';
import {
  resetDraftUnreadIds,
  setActivePostDraftId,
  setEditableCommentDraft,
  setEditablePostDraft,
} from 'redux-store/slices/draft-slice';
import { getActivePostDraftId } from 'redux-store/selector';

import { handleEditorKeyChange } from 'redux-store/slices/side-panel-slice';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetAllProjectsQuery } from 'redux-store/api/projects';
import { DraftDeletionConfirmDialog } from '../draft-deletion-confirm-dialog';
import { getDraftDetails } from './utils';
import { IDraftsList } from './types';
import {
  ActivityFeedDraftTypeEnum,
  IActivityFeedDraftComment,
  IActivityFeedDraftPost,
  TActivityFeedDraftItem,
} from 'redux-store/api/activity-feed';
import { styles } from './styles';

export const DraftsList = ({ drafts, onClose }: IDraftsList) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const [draftToBeDeleted, setDraftToBeDeleted] = useState<TActivityFeedDraftItem>(null);

  const { handleOpenActivityFeed, handleExpandeEditor, isSidePanelOpen } = useSidePanel();

  const activePostDraftId = useAppSelector(getActivePostDraftId);

  const { data: userMe } = useGetMeQuery();
  const { data: allProjects, isLoading: isAllProjectsLoading } = useGetAllProjectsQuery(
    { profileId: userMe?.id },
    { skip: !userMe?.id }
  );

  useEffect(() => {
    dispatch(resetDraftUnreadIds());
    return () => {
      dispatch(resetDraftUnreadIds());
    };
  }, []);

  const isOnProjectDashboardPage = () => {
    return pathname.includes('project-dashboard');
  };

  const handleCompletePost = (draft: IActivityFeedDraftPost) => {
    const ACTIVE_POST_DRAFT_ID_DELAY = 100;

    const updatedDraft = {
      ...draft,
      postVisibility: draft.data.postVisibility,
    };

    const onDashboard = isOnProjectDashboardPage();
    dispatch(setEditablePostDraft(updatedDraft));
    !isSidePanelOpen && handleOpenActivityFeed();
    handleExpandeEditor(true);
    onDashboard && isSidePanelOpen && dispatch(handleEditorKeyChange());
    !onDashboard && navigate(`/project-dashboard/${draft.projectId}`);
    setTimeout(() => {
      dispatch(setActivePostDraftId(draft.id));
    }, ACTIVE_POST_DRAFT_ID_DELAY);
    onClose();
  };

  const handleCompleteComment = (draft: IActivityFeedDraftComment) => {
    const updatedDraft = {
      ...draft.data,
      status: draft.status,
      type: draft.type,
      projectId: draft.projectId,
    };

    dispatch(setEditableCommentDraft(updatedDraft));
    navigate(`/project-dashboard/${draft.projectId}?postId=${draft.data.postId}`);
    onClose();
  };

  const onComplete = (draft: TActivityFeedDraftItem) => {
    draft.type === ActivityFeedDraftTypeEnum.POST
      ? handleCompletePost(draft)
      : handleCompleteComment(draft);
  };

  const onDelete = (draft: TActivityFeedDraftItem) => {
    setDraftToBeDeleted(draft);
  };

  return (
    <>
      <Grid sx={styles.cardsContainer}>
        {[...drafts]
          .sort((a, b) => a.projectId - b.projectId)
          .map((draft: TActivityFeedDraftItem, index: number, arr: TActivityFeedDraftItem[]) => {
            const {
              content,
              media,
              hasMedia,
              hasContent,
              hasMediaOrContent,
              postfix,
              project,
              uploads,
              completePrefix,
              isDraftActive,
            } = getDraftDetails({ draft, allProjects, activePostDraftId });

            const isFirstOfProject =
              index === arr.findIndex((d) => d.projectId === draft.projectId);

            const headerProps = isFirstOfProject
              ? {
                  isLoading: isAllProjectsLoading,
                  name: project.name,
                  location: project.location,
                  createdAt: project.createdAt,
                }
              : undefined;

            return (
              <DraftCard
                key={draft.id}
                headerProps={headerProps}
                footerProps={{
                  postfix,
                  isDraftActive,
                  completePrefix,
                  onComplete: () => onComplete(draft),
                  onDelete: () => onDelete(draft),
                }}
              >
                {hasMediaOrContent && (
                  <Grid>
                    {!isFirstOfProject && (
                      <Typography sx={styles.createdAtTypo}>{project.createdAt}</Typography>
                    )}
                    <Grid sx={styles.contentMediaContent}>
                      {hasMedia && <DraftCardMedia media={media} hasContent={hasContent} />}
                      {hasContent && <DraftCardMessage message={content} />}
                    </Grid>
                  </Grid>
                )}
                <UploadedAudioContent uploadedMultimedia={uploads} />
                <UploadedFiles type="activity_feed" uploads={uploads} />
              </DraftCard>
            );
          })}
      </Grid>
      {!!draftToBeDeleted && (
        <DraftDeletionConfirmDialog
          draft={draftToBeDeleted}
          onClose={() => {
            setDraftToBeDeleted(null);
          }}
        />
      )}
    </>
  );
};
